import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useApp } from "../context/AppContext";
import { Navigate } from "react-router-dom";
import "./AdminDashboard.css";

const ADMIN_EMAIL = "shaileshkamble771995@gmail.com";

export default function AdminDashboard() {
  const { user, loading: authLoading } = useApp();
  const [stats, setStats] = useState({
    totalMechanics: 0,
    verifiedMechanics: 0,
    pendingMechanics: 0,
    totalRiders: 0,
    totalBlogs: 0,
    totalFeedback: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [pendingMechanics, setPendingMechanics] = useState([]);
  const [allRiders, setAllRiders] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]);
  const [approving, setApproving] = useState({});
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      loadDashboardData();
    }
  }, [user]);

  async function loadDashboardData() {
    setLoading(true);
    try {
      // Fetch statistics
      const [mechanics, riders, blogs, feedback] = await Promise.all([
        supabase.from("mechanics").select("*", { count: "exact" }),
        supabase.from("profiles").select("*", { count: "exact" }),
        supabase.from("blog_posts").select("*", { count: "exact" }),
        supabase.from("feedback").select("*", { count: "exact" }),
      ]);

      const verified = mechanics.data?.filter((m) => m.is_verified).length || 0;
      const pending = mechanics.data?.filter((m) => !m.is_verified).length || 0;

      setStats({
        totalMechanics: mechanics.count || 0,
        verifiedMechanics: verified,
        pendingMechanics: pending,
        totalRiders: riders.count || 0,
        totalBlogs: blogs.count || 0,
        totalFeedback: feedback.count || 0,
      });

      // Fetch pending mechanics
      if (mechanics.data) {
        setPendingMechanics(
          mechanics.data.filter((m) => !m.is_verified).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        );
      }

      // Fetch all riders
      if (riders.data) {
        setAllRiders(riders.data);
      }

      // Fetch all blogs
      if (blogs.data) {
        setAllBlogs(blogs.data);
      }

      console.log("✅ Dashboard data loaded");
    } catch (err) {
      setToast(`Error loading data: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function approveMechanic(mechanicId) {
    setApproving((prev) => ({ ...prev, [mechanicId]: true }));
    try {
      const { error } = await supabase
        .from("mechanics")
        .update({ is_verified: true })
        .eq("id", mechanicId);

      if (error) throw error;

      setToast("✅ Mechanic approved!");
      setPendingMechanics((prev) => prev.filter((m) => m.id !== mechanicId));
      setStats((prev) => ({
        ...prev,
        pendingMechanics: prev.pendingMechanics - 1,
        verifiedMechanics: prev.verifiedMechanics + 1,
      }));

      setTimeout(() => setToast(""), 3000);
    } catch (err) {
      setToast(`❌ Error: ${err.message}`);
    } finally {
      setApproving((prev) => ({ ...prev, [mechanicId]: false }));
    }
  }

  async function rejectMechanic(mechanicId) {
    if (!window.confirm("Delete this mechanic permanently?")) return;

    try {
      const { error } = await supabase
        .from("mechanics")
        .delete()
        .eq("id", mechanicId);

      if (error) throw error;

      setToast("❌ Mechanic deleted");
      setPendingMechanics((prev) => prev.filter((m) => m.id !== mechanicId));
      setStats((prev) => ({
        ...prev,
        totalMechanics: prev.totalMechanics - 1,
        pendingMechanics: prev.pendingMechanics - 1,
      }));
    } catch (err) {
      setToast(`Error: ${err.message}`);
    }
  }

  if (authLoading) {
    return <div className="admin-page">Loading...</div>;
  }

  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1>🔐 Admin Dashboard</h1>
          <p className="admin-subheader">System Management & Analytics</p>
        </div>

        {toast && <div className="admin-toast">{toast}</div>}

        {/* Statistics Cards */}
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="stat-icon">🏍️</div>
            <div className="stat-content">
              <h3>Total Mechanics</h3>
              <p className="stat-number">{stats.totalMechanics}</p>
              <p className="stat-detail">
                ✅ {stats.verifiedMechanics} verified • ⏳ {stats.pendingMechanics} pending
              </p>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <h3>Total Riders</h3>
              <p className="stat-number">{stats.totalRiders}</p>
              <p className="stat-detail">Active users in system</p>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-content">
              <h3>Total Blogs</h3>
              <p className="stat-number">{stats.totalBlogs}</p>
              <p className="stat-detail">Published articles</p>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon">💬</div>
            <div className="stat-content">
              <h3>Feedback</h3>
              <p className="stat-number">{stats.totalFeedback}</p>
              <p className="stat-detail">User submissions</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            📊 Overview
          </button>
          <button
            className={`admin-tab ${activeTab === "mechanics" ? "active" : ""}`}
            onClick={() => setActiveTab("mechanics")}
          >
            ⏳ Pending Mechanics ({stats.pendingMechanics})
          </button>
          <button
            className={`admin-tab ${activeTab === "riders" ? "active" : ""}`}
            onClick={() => setActiveTab("riders")}
          >
            👥 Riders ({stats.totalRiders})
          </button>
          <button
            className={`admin-tab ${activeTab === "blogs" ? "active" : ""}`}
            onClick={() => setActiveTab("blogs")}
          >
            📝 Blogs ({stats.totalBlogs})
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="admin-section">
            <h2>System Overview</h2>
            <div className="admin-overview">
              <div className="overview-item">
                <h3>🚀 Quick Stats</h3>
                <ul>
                  <li>Total Mechanics: <strong>{stats.totalMechanics}</strong></li>
                  <li>Verified Mechanics: <strong>{stats.verifiedMechanics}</strong></li>
                  <li>Pending Approval: <strong className="warning">{stats.pendingMechanics}</strong></li>
                  <li>Total Riders: <strong>{stats.totalRiders}</strong></li>
                  <li>Total Blogs: <strong>{stats.totalBlogs}</strong></li>
                  <li>Feedback Received: <strong>{stats.totalFeedback}</strong></li>
                </ul>
              </div>

              <div className="overview-item">
                <h3>⚠️ Action Items</h3>
                <ul>
                  {stats.pendingMechanics > 0 && (
                    <li>
                      <strong>{stats.pendingMechanics}</strong> mechanics awaiting approval{" "}
                      <button className="link-btn" onClick={() => setActiveTab("mechanics")}>
                        Review →
                      </button>
                    </li>
                  )}
                  {stats.pendingMechanics === 0 && <li>✅ All mechanics approved!</li>}
                </ul>
              </div>

              <div className="overview-item">
                <h3>📈 System Health</h3>
                <ul>
                  <li>Database: <strong style={{ color: "green" }}>✅ Connected</strong></li>
                  <li>API: <strong style={{ color: "green" }}>✅ Operational</strong></li>
                  <li>Authentication: <strong style={{ color: "green" }}>✅ Active</strong></li>
                  <li>Last Sync: <strong>Just now</strong></li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === "mechanics" && (
          <div className="admin-section">
            <h2>Pending Mechanic Approvals ({stats.pendingMechanics})</h2>
            {loading ? (
              <p>Loading...</p>
            ) : pendingMechanics.length === 0 ? (
              <p className="empty-state">✅ All mechanics are approved!</p>
            ) : (
              <div className="mechanics-approval-grid">
                {pendingMechanics.map((mechanic) => (
                  <div key={mechanic.id} className="approval-card">
                    <div className="approval-card-header">
                      <h3>{mechanic.name}</h3>
                      <span className="status-badge pending">⏳ Pending</span>
                    </div>

                    <div className="approval-card-body">
                      <p><strong>Shop:</strong> {mechanic.shop_name}</p>
                      <p><strong>Location:</strong> {mechanic.city}, {mechanic.district}, {mechanic.state}</p>
                      <p><strong>Phone:</strong> {mechanic.phone_whatsapp}</p>
                      <p><strong>Specialization:</strong> {mechanic.specialization.join(", ")}</p>
                      <p><strong>Services:</strong> {mechanic.services.join(", ")}</p>
                      <p><strong>Hours:</strong> {mechanic.open_time} - {mechanic.close_time}</p>
                      <p><strong>Submitted:</strong> {new Date(mechanic.created_at).toLocaleDateString()}</p>
                      {mechanic.verification_notes && (
                        <p><strong>Notes:</strong> {mechanic.verification_notes}</p>
                      )}
                    </div>

                    <div className="approval-card-actions">
                      <button
                        className="btn-approve"
                        onClick={() => approveMechanic(mechanic.id)}
                        disabled={approving[mechanic.id]}
                      >
                        {approving[mechanic.id] ? "✅ Approving..." : "✅ Approve"}
                      </button>
                      <button
                        className="btn-reject"
                        onClick={() => rejectMechanic(mechanic.id)}
                      >
                        ❌ Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "riders" && (
          <div className="admin-section">
            <h2>Riders Management ({stats.totalRiders})</h2>
            {loading ? (
              <p>Loading...</p>
            ) : (
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>City</th>
                      <th>Bike</th>
                      <th>Role</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRiders.slice(0, 50).map((rider) => (
                      <tr key={rider.id}>
                        <td>{rider.name || "N/A"}</td>
                        <td>{rider.email || "N/A"}</td>
                        <td>{rider.city || "N/A"}</td>
                        <td>{rider.bike || "N/A"}</td>
                        <td><span className="badge">{rider.role || "rider"}</span></td>
                        <td>{new Date(rider.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "blogs" && (
          <div className="admin-section">
            <h2>Blogs Management ({stats.totalBlogs})</h2>
            {loading ? (
              <p>Loading...</p>
            ) : (
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Author</th>
                      <th>Category</th>
                      <th>Published</th>
                      <th>Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBlogs.slice(0, 50).map((blog) => (
                      <tr key={blog.id}>
                        <td>{blog.title || "N/A"}</td>
                        <td>{blog.author || "N/A"}</td>
                        <td>{blog.category || "N/A"}</td>
                        <td>{new Date(blog.created_at).toLocaleDateString()}</td>
                        <td>{blog.views || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
